import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { EmployeDialog } from "@/components/employes/EmployeDialog";
import { useEmployes, EtatBadge } from "@/components/employes/shared";
import { Users, Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const HEAD = ["Nom", "Société", "Poste", "Contrat", "Date d'embauche", "Fin d'embauche", "Statut"];

const Row = ({ e, editable, onEdit, onDelete }) => (
  <tr data-testid={`employe-row-${e.id}`}>
    <td className="py-2 pr-2 font-medium">{e.nom} {e.prenom}</td>
    <td className="py-2 pr-2 text-xs">{e.societe_nom}</td>
    <td className="py-2 pr-2">{e.poste || "—"}</td>
    <td className="py-2 pr-2">{e.type_contrat}</td>
    <td className="py-2 pr-2">{fmtDate(e.date_embauche)}</td>
    <td className="py-2 pr-2">{e.date_fin ? fmtDate(e.date_fin) : "—"}</td>
    <td className="py-2 pr-2">{e.date_fin ? <EtatBadge statut={e.statut_contrat} jours={e.jours_restants} testid={`employe-statut-${e.id}`} /> : <span className="text-xs text-emerald-700">En poste</span>}</td>
    <td className="py-2 text-right whitespace-nowrap">
      {editable && <>
        <Button size="sm" variant="ghost" onClick={() => onEdit(e)} data-testid={`employe-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(e)} data-testid={`employe-delete-${e.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
      </>}
    </td>
  </tr>
);

export default function Employes() {
  const { items, load, editable } = useEmployes();
  const [edit, setEdit] = useState(null);

  const onDelete = async (e) => {
    if (!window.confirm(`Supprimer ${e.nom} ${e.prenom} et tous ses documents ?`)) return;
    await api.delete(`/employes/${e.id}`);
    toast.success("Employé supprimé");
    load();
  };

  return (
    <div data-testid="employes-page">
      <PageHeader title="Dates d'embauche" subtitle="Embauches et fins de contrat — alerte 30 jours avant la fin"
        actions={editable && <Button className="gap-1" onClick={() => setEdit({})} data-testid="employe-add-btn"><Plus className="h-4 w-4" /> Nouvel employé</Button>} />
      <div className="rounded-xl border bg-card p-5">
        {items === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : !items.length ? <EmptyState icon={Users} title="Aucun employé" hint="Ajoutez vos employés pour suivre leurs contrats, documents et congés." />
            : <div className="overflow-x-auto"><table className="w-full text-sm min-w-[900px]">
              <thead><tr className="text-left text-muted-foreground border-b">{HEAD.map((h) => <th key={h} className="py-2 pr-2 font-medium">{h}</th>)}<th /></tr></thead>
              <tbody className="divide-y">{items.map((e) => <Row key={e.id} e={e} editable={editable} onEdit={setEdit} onDelete={onDelete} />)}</tbody>
            </table></div>}
      </div>
      {edit && <EmployeDialog employe={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
